import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { LoginResponseModel } from '../models/loginResponse.model';
import { MFAResponseModel } from '../models/mfaResponse.model';
import { RefreshResponseModel } from '../models/refreshResponse.model';
import { PublicKeyResponseModel } from '../models/publicKeyResponse.model';
import { decodeJwt } from '../shared/jwt';
import { ResendOTPResponseModel } from '../models/resendOTPResponse.model';

export type ServerRegion = 'US' | 'UK-EU';

/** Roles as the auth service issues them. */
export type Role = 'SuperAdmin' | 'SUPPORT_USER' | 'AccountUser' | 'BUSINESS_USER';

/** Most privileged first — the order `role` picks a display label from. */
const ROLE_PRECEDENCE: readonly Role[] = ['SuperAdmin', 'SUPPORT_USER', 'AccountUser', 'BUSINESS_USER'];

/** Friendly display labels for roles. */
export const ROLE_LABELS: Record<Role, string> = {
  'SuperAdmin': 'Super Admin',
  'SUPPORT_USER': 'Support User',
  'AccountUser': 'Account User',
  'BUSINESS_USER': 'Business User'
};

/** Data-access scope levels, broadest first. */
export type ScopeLevel = 'ROOT' | 'BUSINESS' | 'COUNTRY' | 'STATE' | 'SITE';

/** One entry of the access token's `scope` claim. */
export interface ScopeGrant {
  businessId: string;
  /** Granularity of the grant — one of the ScopeLevel values. */
  scopeLevel: string;
  scopeValueId: string;
}

/**
 * Claims carried by the access token.
 *
 * The profile claims are optional: tokens minted before the service began
 * sending them carry none, and a session can outlive that change.
 */
export interface AccessTokenClaims {
  sub: string;
  email: string;
  firstName?: string;
  lastName?: string;
  /** Human-readable business name — what the UI shows as the processor. */
  companyName?: string;
  /** e.g. "Dealer". Modelled for completeness; nothing reads it yet. */
  businessType?: string;
  /**
   * How many sites the account's reports cover: a count as a string, or the
   * literal 'all' for an unrestricted account. Always a string, never a list —
   * unrelated to `DataScope.sites`, which is a set of site names.
   */
  sites?: string;
  roles: string[];
  permissions: string[];
  scope: ScopeGrant[];
  /** Seconds since the epoch, on the server's clock. */
  iat: number;
  exp: number;
}

/**
 * Geographic scope restricting which scans a user may see (within their
 * processor). An empty/absent dimension means "no restriction on that
 * dimension"; an empty scope object means full processor access.
 */
export interface DataScope {
  countries?: string[];
  states?: string[];
  sites?: string[];
}

/**
 * Profile of the signed-in user.
 *
 * Deliberately holds nothing the access token carries: email, roles and
 * permissions all read from the claims, so a refresh — which returns no
 * profile — keeps them current instead of leaving a stale copy here.
 */
export interface AuthUser {
  region: ServerRegion;
  country: string;
  processorDomain: string;
  scope: DataScope;
}

/** Treat a token as dead this long early, so it can't expire mid-flight. */
const EXPIRY_SKEW_MS = 10_000;

/** How long the credential-encryption key is reused before refetching. */
const PUBLIC_KEY_TTL_MS = 5 * 60 * 1000;

/**
 * Handles authentication, the active server region, role-based access rights,
 * and the bearer token used for API authorization.
 *
 * Sign-in is two steps: `login` sends the one-time code, `verifyMFA` returns
 * the tokens. Authorization is read from the access token's claims rather than
 * stored, so a refresh picks up any change to a user's roles or permissions.
 *
 * Expiry is tracked against the server's clock, anchored to the `timestamp`
 * each response carries and advanced with a monotonic reading rather than the
 * wall clock — so changing the OS time mid-session cannot stretch it. That is a
 * correctness measure, not a security one: the token's `exp` is signed and the
 * server enforces it regardless of what this class believes.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly _http = inject(HttpClient);

  private readonly TOKEN_KEY = 'p4c.auth.token';
  private readonly REFRESH_TOKEN_KEY = 'p4c.auth.token.refresh.key';
  private readonly USER_KEY = 'p4c.auth.user';

  // State initialization driven directly from localStorage persistence
  private readonly _token = signal<string | null>(localStorage.getItem(this.TOKEN_KEY));
  private readonly _refresh_token = signal<string | null>(localStorage.getItem(this.REFRESH_TOKEN_KEY));
  private readonly _user = signal<AuthUser | null>(this.loadUser());

  /**
   * Server time at the last response, paired with a monotonic reading taken at
   * the same moment. In memory only: a monotonic clock resets on page load, so
   * after a reload there is no trustworthy way to measure elapsed time and the
   * first request refreshes instead of trusting a persisted instant.
   */
  private clockAnchor: { serverTime: number; perfMark: number } | null = null;
  private accessTokenExpiresAt: number | null = null;
  private refreshTokenExpiresAt: number | null = null;

  /** The in-flight refresh, shared so concurrent callers trigger only one. */
  private refreshInFlight: Observable<RefreshResponseModel> | null = null;

  /** Credential-encryption key cache, and the fetch shared between callers. */
  private publicKeyCache: { key: string; fetchedAt: number } | null = null;
  private publicKeyRequest: Observable<string> | null = null;

  private readonly _refreshing = signal(false);

  // Public readonly views for responsive application data bindings
  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();

  /** Claims of the current access token, re-derived whenever it changes. */
  readonly claims = computed<AccessTokenClaims | null>(() => {
    const token = this._token();
    return token ? decodeJwt<AccessTokenClaims>(token) : null;
  });

  /** The signed-in user's email, from the token so a refresh keeps it current. */
  readonly email = computed<string>(() => this.claims()?.email ?? this.claims()?.sub ?? '');

  /** Roles the token grants, ignoring any name this app does not know. */
  readonly roles = computed<Role[]>(() =>
    (this.claims()?.roles ?? []).filter((role): role is Role => ROLE_PRECEDENCE.includes(role as Role))
  );

  /** Highest-precedence role held, for display. */
  readonly role = computed<Role | null>(() => {
    const held = this.roles();
    return ROLE_PRECEDENCE.find(candidate => held.includes(candidate)) ?? null;
  });

  readonly roleLabel = computed<string>(() => {
    const role = this.role();
    return role ? ROLE_LABELS[role] : '';
  });

  /** Given and family name, or '' when the token predates those claims. */
  readonly fullName = computed<string>(() => {
    const claims = this.claims();
    return [claims?.firstName, claims?.lastName].filter(Boolean).join(' ');
  });

  /** Whatever best identifies the user on screen, falling back to their email. */
  readonly displayName = computed<string>(() => this.fullName() || this.email());

  /** The account's business type, e.g. "Dealer". */
  readonly businessType = computed<string>(() => this.claims()?.businessType ?? '');

  readonly region = computed<ServerRegion>(() => this._user()?.region ?? 'US');

  /**
   * The business the account belongs to. Read from the token rather than the
   * stored profile, so a refresh keeps it current — and because nothing in any
   * response body carries it.
   */
  readonly processor = computed<string>(() => this.claims()?.companyName ?? '');
  readonly processorLabel = computed<string>(() => this.processor() || 'All Processors');

  /**
   * The raw `sites` claim — 'all', a count as a string, or '' on a token
   * minted before the claim existed. Left unformatted here: the display form
   * needs the user's locale, which lives in the settings service.
   */
  readonly sites = computed<string>(() => this.claims()?.sites ?? '');

  readonly scope = computed<DataScope>(() => this._user()?.scope ?? {});

  /**
   * User Management is open to Super Admins and to accounts with a Root- or
   * Business-level data-access scope. Which users they see is decided
   * server-side from the same token.
   */
  readonly canManageUsers = computed(() =>
    this.hasRole('SuperAdmin') || this.hasScopeLevel('ROOT') || this.hasScopeLevel('BUSINESS')
  );

  readonly refreshing = this._refreshing.asReadonly();

  /**
   * Whether a request may be attempted.
   *
   * Permissive on purpose: after a reload there is no trustworthy clock, so a
   * held refresh token counts as recoverable and the server is left to rule on
   * it. Route guards use this; the interceptor uses hasLiveAccessToken().
   */
  isAuthenticated(): boolean {
    return this.hasLiveAccessToken() || this._refresh_token() !== null;
  }

  /** Whether the access token is known-good — drives the proactive refresh. */
  hasLiveAccessToken(): boolean {
    const now = this.serverNow();
    return now !== null
      && this.accessTokenExpiresAt !== null
      && now < this.accessTokenExpiresAt - EXPIRY_SKEW_MS;
  }

  /** Whether the refresh token is worth presenting. */
  canRefresh(): boolean {
    if (this._refresh_token() === null) {
      return false;
    }
    const now = this.serverNow();
    // Unanchored, i.e. straight after a reload: let the server decide.
    return now === null
      || this.refreshTokenExpiresAt === null
      || now < this.refreshTokenExpiresAt - EXPIRY_SKEW_MS;
  }

  /**
   * SuperAdmin overrides every restriction; everyone else is limited to the
   * grants their token carries.
   */
  hasPermission(permission: string): boolean {
    return this.hasRole('SuperAdmin') || (this.claims()?.permissions.includes(permission) ?? false);
  }

  /** Membership in the full set of roles, not just the highest one. */
  hasRole(role: Role): boolean {
    return this.roles().includes(role);
  }

  /** Whether any of the token's data-access grants is at this level. */
  hasScopeLevel(level: ScopeLevel): boolean {
    return this.claims()?.scope?.some(grant => grant.scopeLevel === level) ?? false;
  }

  forgotPassword(email: string): Observable<any> {
    return this._http.post(environment.authEndpoint + environment.authPWForgot, {
      email: email
    });
  }

  resetPassword(email: string, otp: string, password: string): Observable<any> {
    return this._http.post(environment.authEndpoint + environment.authPWReset, { email, otp, password });
  }

  /** Raw fetch of the credential-encryption key. Unauthenticated by design. */
  getPublicKey(): Observable<PublicKeyResponseModel> {
    return this._http.get<PublicKeyResponseModel>(environment.authEndpoint + environment.authPublicKeyPath);
  }

  /**
   * The credential-encryption key, from cache while it is fresh.
   *
   * The key is static in normal operation, so the cache exists to keep sign-in
   * to a single round trip rather than to hide rotation. Memory only — never
   * persisted. Concurrent callers share one fetch: a sign-in seals two fields
   * and would otherwise ask twice.
   */
  encryptionPublicKey(): Observable<string> {
    const cached = this.publicKeyCache;
    if (cached && Date.now() - cached.fetchedAt < PUBLIC_KEY_TTL_MS) {
      return of(cached.key);
    }

    if (!this.publicKeyRequest) {
      this.publicKeyRequest = this.getPublicKey().pipe(
        map(response => {
          if (!response.success || !response.data) {
            throw new Error(response.message || 'Encryption key unavailable.');
          }
          return response.data;
        }),
        tap(key => { this.publicKeyCache = { key, fetchedAt: Date.now() }; }),
        // Cleared as the call settles — before shareReplay, so a later stale
        // read starts a fresh request rather than replaying this one.
        finalize(() => { this.publicKeyRequest = null; }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.publicKeyRequest;
  }

  /** Drops the cached key so the next request fetches a fresh one. */
  invalidateEncryptionKey(): void {
    this.publicKeyCache = null;
    this.publicKeyRequest = null;
  }

  /**
   * First sign-in step: validates the credentials and dispatches the one-time
   * code. No tokens come back here — the session starts at verifyMFA.
   */
  login(email: string, password: string): Observable<LoginResponseModel> {
    return this._http.post<LoginResponseModel>(environment.authEndpoint + environment.authLoginPath, { email, password }).pipe(
      tap(response => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Authentication failed.');
        }
      })
    );
  }

  resendOTP(email: string): Observable<ResendOTPResponseModel> {
    return this._http.post<ResendOTPResponseModel>(environment.authEndpoint + environment.authResendOTPPath, { email }).pipe(
      tap(response => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Resend OTP Failed');
        }
      })
    );
  }

  /** Second sign-in step: exchanges the one-time code for tokens. */
  verifyMFA(email: string, otp: string, region: ServerRegion): Observable<MFAResponseModel> {
    return this._http.post<MFAResponseModel>(environment.authEndpoint + environment.authMFAPath, { email, otp }).pipe(
      tap(response => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Verification failed.');
        }
        this.storeSession(response, region);
      })
    );
  }

  /**
   * Exchanges the refresh token for a new pair.
   *
   * Shared, not repeated: a page load fires several requests at once, all of
   * them against an unanchored clock. Without this they would each start a
   * refresh, and every one but the first would present a token the winner had
   * already rotated away.
   */
  refreshSession(): Observable<RefreshResponseModel> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshToken = this._refresh_token();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token.'));
    }

    this.refreshInFlight = defer(() => {

      this._refreshing.set(true);

      return this._http.post<RefreshResponseModel>(environment.authEndpoint + environment.authRefreshPath, { refreshToken })

    }).pipe(
      tap(response => {
        const data = response.data;
        if (!response.success || !data?.accessToken) {
          // A 200 carrying no tokens: the server declined the refresh token
          // rather than failing at the transport. Logged for diagnosis — the
          // server's wording is developer-facing.
          console.warn('Token refresh declined:', response.message);
          throw new Error('Your session has expired. Please sign in again.');
        }
        this.commitTokens(
          data.accessToken,
          data.refreshToken,
          data.accessTokenExpiresIn,
          data.refreshTokenExpiresIn,
          response.timestamp
        );
      }),
      catchError(err => {
        // However the refresh failed — a transport error, or a 200 that
        // declined it — the session cannot be recovered, so end it here. The
        // interceptor cannot infer this itself: it keys off HTTP status, and
        // the declined case never produces one.
        this.logout();
        this._refreshing.set(false);
        return throwError(() => err);
      }),
      // Cleared as the call settles — before shareReplay, so the next expiry
      // starts a fresh request rather than replaying this one.
      finalize(() => {
        this._refreshing.set(false);
        this.refreshInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refreshInFlight;
  }

  /** Purges current local session memory and shifts control back to login view. */
  logout(): void {
    const accessToken = this._token();
    const refreshToken = this._refresh_token();

    // Cleared before the revoke so a 401 on that call cannot re-enter through
    // the interceptor and start a loop — the second pass posts nothing.
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    this._token.set(null);
    this._refresh_token.set(null);
    this._user.set(null);
    this.clockAnchor = null;
    this.accessTokenExpiresAt = null;
    this.refreshTokenExpiresAt = null;
    this._refreshing.set(false);
    this.refreshInFlight = null;

    if (accessToken) {
      this._http.post(environment.authEndpoint + environment.authLogoutPath, {
        accessToken,
        refreshToken
      }).subscribe({ error: () => { /* the session is already gone locally */ } });
    }

    this.router.navigate(['/login']);
  }

  /** Commits the tokens and profile a verified sign-in carries. */
  private storeSession(response: MFAResponseModel, region: ServerRegion): void {
    const data = response.data;
    const claims = decodeJwt<AccessTokenClaims>(data.accessToken);
    const email = claims?.email ?? claims?.sub ?? data.email;

    const authUser: AuthUser = {
      region: region,
      country: data.country ?? '',
      processorDomain: email.split('@')[1] || '',
      scope: {
        countries: data.country ? [data.country] : []
      }
    };

    localStorage.setItem(this.USER_KEY, JSON.stringify(authUser));
    this._user.set(authUser);

    this.commitTokens(
      data.accessToken,
      data.refreshToken,
      data.accessTokenExpiresIn,
      data.refreshTokenExpiresIn,
      response.timestamp
    );
  }

  /**
   * Records a token pair and re-anchors the clock from the server's own
   * timestamp.
   *
   * The access token's expiry comes from its signed `exp` — the value the
   * server actually enforces — falling back to the stated lifetime if a token
   * ever arrives without one. The opaque refresh token has no `exp`, so its
   * window is measured from the response instead; rotation resets it.
   */
  private commitTokens(
    accessToken: string,
    refreshToken: string,
    accessTokenExpiresIn: number,
    refreshTokenExpiresIn: number,
    timestamp: string
  ): void {
    const parsed = Date.parse(timestamp);
    const serverTime = Number.isNaN(parsed) ? Date.now() : parsed;
    const claims = decodeJwt<AccessTokenClaims>(accessToken);

    this.clockAnchor = { serverTime, perfMark: performance.now() };
    this.accessTokenExpiresAt = claims?.exp
      ? claims.exp * 1000
      : serverTime + accessTokenExpiresIn;
    this.refreshTokenExpiresAt = serverTime + refreshTokenExpiresIn;

    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);

    this._token.set(accessToken);
    this._refresh_token.set(refreshToken);
  }

  /**
   * Estimated server time, advanced with a monotonic reading so a change to
   * the OS clock has no effect. Null until a response has anchored it.
   */
  private serverNow(): number | null {
    const anchor = this.clockAnchor;
    return anchor ? anchor.serverTime + (performance.now() - anchor.perfMark) : null;
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }
}
